import { Component, Input, Output, EventEmitter, ViewChild, OnInit, OnChanges, SimpleChanges, AfterViewInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AccordionModule } from 'primeng/accordion';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { TableModule, Table } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import  moment  from 'moment';
import { getSvgIcon } from '../utils/svg-icons.utils';

// Interfaces
export interface FilterConfig {
  key: string;
  type: 'text' | 'date' | 'select' | 'number' | 'autocomplete';
  placeholder?: string;
  label?: string;
  options?: any[];
  suggestions?: any[];
  optionLabel?: string;
  optionValue?: string;
  value?: any;
  showInAccordion?: boolean;
  showInDialog?: boolean;
  showInSidebar?: boolean;
  // For lazy loaded selects
  lazy?: boolean;
  filter?: boolean;
  virtualScroll?: boolean;
  virtualScrollItemSize?: number;
  filterPlaceholder?: string;
  loading?: boolean;
  onFilter?: (event: any) => void;
  onLazyLoad?: (event: any) => void;
  onChange?: (event: any) => void;
  // For autocomplete/searchable selects
  completeMethod?: (event: { query: string; filter: FilterConfig }) => void | Promise<void>;
  dropdown?: boolean;
  forceSelection?: boolean;
}

export interface ColumnConfig {
  key: string;
  header: string;
  type?: 'text' | 'date' | 'datetime' | 'currency' | 'number' | 'status' | 'custom';
  format?: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  ellipsis?: boolean;
  customTemplate?: string;
  customField?: boolean;
  statusConfig?: {
    [key: string]: string | undefined;
  };
}
 
export interface ActionConfig {
  key: string;
  label: string;
  icon?: string;
  svgIcon?: string;
  tooltip?: string;
  severity?: 'primary' | 'secondary' | 'success' | 'info' | 'danger' | 'help' | 'contrast';
  show?: (row: any) => boolean;
  disabled?: (row: any) => boolean;
  type?: 'button' | 'toggle';
}

export interface FilterEvent {
  data?: any;
  filterKey?: string;
  searchValue?: string;
  filter?: FilterConfig;
}

export interface ActionEvent {
  action: string;
  row: any;
  rowIndex: number;
}

@Component({
  selector: 'lib-dynamic-listing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    AccordionModule,
    DatePickerModule,
    SelectModule,
    AutoCompleteModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
    DialogModule,
    DrawerModule,
    CheckboxModule,
    ToggleSwitchModule,
    AutoCompleteModule
  ],
  templateUrl: './dynamic-listing.html',
  styleUrl: './dynamic-listing.css',
})
export class DynamicListingComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('dt') dt!: Table;

  private sanitizer = inject(DomSanitizer);

  // Inputs
  @Input() module: string = '';
  @Input() filters: FilterConfig[] = [];
  @Input() columns: ColumnConfig[] = [];
  @Input() data: any[] = [];
  @Input() actions: ActionConfig[] = [];
  @Input() headerActions: ActionConfig[] = [];
  @Input() isLoading: boolean = false;
  @Input() pageIndex: number = 1;
  @Input() rowsPerPage: number = 10;
  @Input() totalCount: number = 0;
  @Input() totalPages: number = 0;
  @Input() exportFilename: string = 'export';
  @Input() showExportButtons: boolean = true;
  @Input() showPagination: boolean = true;
  @Input() showIncludeInactiveCheckbox: boolean = false;
  private _includeInactive: boolean = false;
  
  @Input() 
  set includeInactive(value: boolean) {
    if (this._includeInactive !== value) {
      this._includeInactive = Boolean(value); // Ensure it's a boolean
      this.internalIncludeInactive = Boolean(value);
    }
  }
  
  get includeInactive(): boolean {
    return this._includeInactive;
  }

  // Outputs
  @Output() filterEvent = new EventEmitter<FilterEvent>();
  @Output() actionClicked = new EventEmitter<ActionEvent>();
  @Output() pageChanged = new EventEmitter<number>();
  @Output() exportCsv = new EventEmitter<void>();
  @Output() exportXlsx = new EventEmitter<void>();
  @Output() exportPdf = new EventEmitter<void>();
  @Output() columnsSelected = new EventEmitter<string[]>();
  @Output() includeInactiveChanged = new EventEmitter<boolean>();

  // Internal state
  filterValues: { [key: string]: any } = {};
  displayFilterDialog: boolean = false;
  displayFilterSidebar: boolean = false;
  selectedColumns: string[] = []; // Track selected column keys
  internalIncludeInactive: boolean = false; // Internal state for checkbox
  orderedColumns: ColumnConfig[] = []; // Track columns in display order

  ngOnInit() {
    this.initializeFilterValues();
    this.initializeSelectedColumns();
    this.internalIncludeInactive = Boolean(this.includeInactive);
  }

  private initializeSelectedColumns() {
    // Initialize with all columns selected by default
    this.selectedColumns = this.columns.map(col => col.key);
    // Initialize ordered columns with all columns in their original order
    this.orderedColumns = [...this.columns];
  }


  ngAfterViewInit() {
    // Ensure checkbox value is set after view initialization
    this.internalIncludeInactive = Boolean(this.includeInactive);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filters']) {
      // Preserve existing values when filters change
      const oldValues = { ...this.filterValues };
      this.initializeFilterValues();
      // Restore values that still exist
      this.filters.forEach(filter => {
        if (oldValues[filter.key] !== undefined) {
          this.filterValues[filter.key] = oldValues[filter.key];
        }
      });
    }
    if (changes['columns']) {
      // Initialize selected columns when columns change
      this.initializeSelectedColumns();
      // Update ordered columns
      this.orderedColumns = [...this.columns];
    }

    if(changes['module']) {
      this.loadModuleAllowedColumns();
    }
  }

  loadModuleAllowedColumns() { 
    // TODO: Load allowed columns for the module from backend 

    this.selectedColumns = this.columns.map(col => col.key);
    this.orderedColumns = [...this.columns];
  }

  private initializeFilterValues() {

    this.filters.forEach(filter => {
      if (filter.value !== undefined) {
        this.filterValues[filter.key] = filter.value;
      } else {
        this.filterValues[filter.key] = filter.type === 'date' ? null : '';
      }
    });
  }

  applyFilters() {
    const filterData: any = {};
    this.filters.forEach(filter => {
      filterData[filter.key] = this.filterValues[filter.key];
    });
    
    this.filterEvent.emit({
      data: filterData
    });

    this.displayFilterDialog = false;
    this.displayFilterSidebar = false; // Close sidebar when filters are applied
  }

  applyColumnSelection() {
    // Emit selected columns when Apply button is clicked in drawer
    this.columnsSelected.emit([...this.selectedColumns]);
    this.displayFilterSidebar = false; // Close drawer
  }

  onIncludeInactiveChange(value: boolean) {
    this.internalIncludeInactive = value;
    this.includeInactiveChanged.emit(value);
  }

  toggleColumnSelection(columnKey: string, isSelected: boolean) {
    if (isSelected) {
      if (!this.selectedColumns.includes(columnKey)) {
        // Add the column at the position it appears in orderedColumns
        const columnIndex = this.orderedColumns.findIndex(col => col.key === columnKey);
        const selectedBefore = this.orderedColumns
          .slice(0, columnIndex)
          .filter(col => this.selectedColumns.includes(col.key))
          .map(col => col.key);
        const selectedAfter = this.orderedColumns
          .slice(columnIndex + 1)
          .filter(col => this.selectedColumns.includes(col.key))
          .map(col => col.key);
        
        this.selectedColumns = [...selectedBefore, columnKey, ...selectedAfter];
      }
    } else {
      // Remove from selectedColumns but keep in orderedColumns
      this.selectedColumns = this.selectedColumns.filter(key => key !== columnKey);
    }
  }

  isColumnSelected(columnKey: string): boolean {
    return this.selectedColumns.includes(columnKey);
  }

  getVisibleColumns(): ColumnConfig[] {
    // Return columns in the order of selectedColumns array
    const columnMap = new Map(this.columns.map(col => [col.key, col]));
    return this.selectedColumns
      .map(key => columnMap.get(key))
      .filter((col): col is ColumnConfig => col !== undefined);
  }

  getOrderedColumnsForPreference(): ColumnConfig[] {
    // Return columns in the order maintained by orderedColumns
    return this.orderedColumns;
  }

  dropColumn(event: CdkDragDrop<ColumnConfig[]>) {
    // Reorder the columns in orderedColumns
    moveItemInArray(this.orderedColumns, event.previousIndex, event.currentIndex);
    
    // Update selectedColumns to match the new order (only for selected columns)
    // This ensures selected columns maintain their new order
    const selectedInOrder = this.orderedColumns
      .filter(col => this.selectedColumns.includes(col.key))
      .map(col => col.key);
    
    // Update selectedColumns maintaining the new order
    this.selectedColumns = selectedInOrder;
  }

  clearFilters() {
    this.filters.forEach(filter => {
      this.filterValues[filter.key] = filter.type === 'date' ? null : '';
    });
    this.applyFilters();
  }

  clearColumnSelection() {
    // Reset to all columns selected
    this.selectedColumns = this.columns.map(col => col.key);
    this.orderedColumns = [...this.columns];
  }

  openFilterSidebar() {
    this.displayFilterSidebar = true;
  }

  closeFilterSidebar() {
    this.displayFilterSidebar = false;
  }

  getFiltersForAccordion(): FilterConfig[] {
    return this.filters.filter(f => f.showInAccordion !== false);
  }

  getFiltersForDialog(): FilterConfig[] {
    return this.filters.filter(f => f.showInDialog === true);
  }

  onActionClick(action: ActionConfig, row: any, rowIndex: number) { // view, edit, delete button clicks
    this.actionClicked.emit({
      action: action.key,
      row,
      rowIndex
    });
  }

  onHeaderActionClick(action: ActionConfig) {
    this.actionClicked.emit({
      action: action.key,
      row: null,
      rowIndex: -1
    });
  }

  loadPreviousPage() {
    if (this.pageIndex <= 1) return;
    this.pageChanged.emit(this.pageIndex - 1);
  }

  loadNextPage() {
    if (this.pageIndex >= this.totalPages) return;
    this.pageChanged.emit(this.pageIndex + 1);
  }

  onExportCsv() {
    
    console.log('Export CSV dynamic-listing clicked');

    this.exportCsv.emit();
  }

  onExportXlsx() {

    console.log('Export XLSX dynamic-listing clicked');

    this.exportXlsx.emit();
  }

  onExportPdf() {
    console.log('Export PDF dynamic-listing clicked');

    this.exportPdf.emit();
  }

  formatCellValue(row: any, column: ColumnConfig, isCustomField?: boolean): string { // returns formatted string value

    let value = row[column.key];
    if (isCustomField) {
      return row.custom_field[column.key];
    } 
    
    if (value === null || value === undefined) {
      return '-';
    }

    // Handle datetime type - format with date and time
    if (column.type === 'datetime') {
      if (moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format('DD/MM/YYYY HH:mm a');
      }
      // Try parsing as date if not ISO format
      const dateValue = moment(value);
      if (dateValue.isValid()) {
        return dateValue.format('DD/MM/YYYY HH:mm a');
      }
      return value?.toString() || '-';
    }

    // Handle date type - format as DD/MM/YYYY
    if (column.type === 'date') {
      if (moment(value, moment.ISO_8601, true).isValid()) {
        return moment(value).format('DD/MM/YYYY');
      }
      // Try parsing as date if not ISO format
      const dateValue = moment(value);
      if (dateValue.isValid()) {
        return dateValue.format('DD/MM/YYYY');
      }
      return value?.toString() || '-';
    }

    // Handle currency type
    if (column.type === 'currency') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numValue);
      }
      return value?.toString() || '-';
    }

    // Handle number type
    if (column.type === 'number') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        return numValue.toLocaleString('en-IN');
      }
      return value?.toString() || '-';
    }

    return value?.toString() || '-';
  }



  
  // Sanitize and return SVG HTML for safe rendering
  getSanitizedSvg(svgString: string | undefined): SafeHtml | null {
    if (!svgString) {
      return null;
    }
    // Use bypassSecurityTrustHtml for SVG content as it's trusted content from our utils
    return this.sanitizer.bypassSecurityTrustHtml(svgString);
  }

  // Get column preference icon
  getColumnPreferenceIcon(): SafeHtml | null {
    const icon = getSvgIcon('COLUMN_PREFERENCE');
    return this.getSanitizedSvg(icon);
  }

  openFilterDialog($event: any) {
    $event.stopPropagation();
    this.displayFilterDialog = true;
  }

  /**
   * Handle dropdown click - trigger search with empty query to show all options
   */
  async onAutocompleteDropdownClick(event: any, filter: FilterConfig) {
    if (filter.completeMethod) {
      try {
        // Trigger search with empty query to show all options
        await filter.completeMethod({ query: '', filter });
        // Update the filter's suggestions after the search
        const filterIndex = this.filters.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filters[filterIndex].suggestions = filter.suggestions || [];
        }
      } catch (error) {
        console.error('Error in autocomplete dropdown click:', error);
      }
    }
  }

  /**
   * Handle Enter key press - trigger search with current input value
   */
  async onAutocompleteEnterKey(event: any, filter: FilterConfig) {
    if (filter.completeMethod) {
      try {
        // Get the current input value
        const inputElement = event.target as HTMLInputElement;
        const query = inputElement?.value || '';
        
        // Trigger search with the current query
        await filter.completeMethod({ query, filter });
        // Update the filter's suggestions after the search
        const filterIndex = this.filters.findIndex(f => f.key === filter.key);
        if (filterIndex !== -1) {
          this.filters[filterIndex].suggestions = filter.suggestions || [];
        }
      } catch (error) {
        console.error('Error in autocomplete Enter key search:', error);
      }
    }
  }

  onAutocompleteSelect(event: any, filter: FilterConfig) {
    // Update filter value when an option is selected
    // PrimeNG autocomplete returns the full selected object, so we need to extract the value
    let filterValue = event;
    if (event && typeof event === 'object' && event !== null) {
      // Check if it has a value property (autocomplete option format: { value: id, label: name })
      if (event.value !== undefined && event.value !== null) {
        filterValue = event.value;
      } else if (event.id !== undefined && event.id !== null) {
        filterValue = event.id;
      } else {
        // If no value/id found, try to extract from optionValue if specified
        if (filter.optionValue && event[filter.optionValue] !== undefined) {
          filterValue = event[filter.optionValue];
        } else {
          // Last resort: keep the object but this might cause issues - log a warning
          console.warn(`Autocomplete filter "${filter.key}" selected object without value/id property:`, event);
          filterValue = event;
        }
      }
    }
    this.filterValues[filter.key] = filterValue;
    // Optionally trigger filter application
    if (filter.onChange) {
      filter.onChange(event);
    }
    // Auto-apply filters when a selection is made (for better UX) 
    this.applyFilters();
  }

  onAutocompleteClear(filter: FilterConfig) {
    // Clear the filter value when the clear button is clicked
    this.filterValues[filter.key] = null;
    // Optionally trigger filter application
    if (filter.onChange) {
      filter.onChange(null);
    }
    // Auto-apply filters when cleared (for better UX)
    this.applyFilters();
  }
}

